import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, Flower2, Sparkles, Timer, Wind, Music4, Salad, Gift } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { t, pickMulti } from "../i18n/translations";
import { api } from "../lib/api";

// Hero images rotate automatically (fetched from /api/hero-images managed by admin)
const FALLBACK_HERO_IMAGES = [
  "https://customer-assets.emergentagent.com/job_massage-portal-6/artifacts/8yrkqmak_1.jpg",
];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

const OIL_IMG = "https://static.prod-images.emergentagent.com/jobs/8c4d7b5e-b128-4051-88e1-db15abcc6df9/images/0a93041f11f44ab9de60b6e59dc5ec9740040a08b263f9bf85676919d2dbd228.png";
const TEXTURE_IMG = "https://static.prod-images.emergentagent.com/jobs/8c4d7b5e-b128-4051-88e1-db15abcc6df9/images/75f9845291896884cdbd9f0ae2512ef8f2fda426d78633dc20521d93eeebd22a.png";

export default function Home() {
  const { lang } = useLang();
  const [services, setServices] = useState([]);
  const [heroImages, setHeroImages] = useState(FALLBACK_HERO_IMAGES);
  const [rotationMs, setRotationMs] = useState(2 * 60 * 60 * 1000);
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
    api.get("/hero-images").then((r) => {
      const imgs = (r.data?.images || []).filter(Boolean);
      const hours = Number(r.data?.rotation_hours) || 2;
      if (imgs.length > 0) {
        setHeroImages(imgs);
        setRotationMs(hours * 60 * 60 * 1000);
      }
    }).catch(() => {});
  }, []);

  // Compute current index whenever images or interval changes, and re-check every minute
  useEffect(() => {
    const compute = () => Math.floor(Date.now() / rotationMs) % heroImages.length;
    setHeroIndex(compute());
    const id = setInterval(() => setHeroIndex(compute()), 60 * 1000);
    return () => clearInterval(id);
  }, [heroImages, rotationMs]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 pt-16 lg:pt-24 pb-20">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-6 animate-fade-up" data-testid="hero-eyebrow">
              {t(lang, "hero.eyebrow")}
            </div>
            <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-balance text-ink animate-fade-up" style={{ animationDelay: "120ms" }} data-testid="hero-title">
              {t(lang, "hero.title")}
            </h1>
            <p className="mt-8 text-lg text-ink-muted max-w-xl leading-relaxed animate-fade-up" style={{ animationDelay: "240ms" }} data-testid="hero-subtitle">
              {t(lang, "hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "340ms" }}>
              <Link to="/booking" className="group inline-flex items-center gap-2 px-7 py-3.5 bg-terracotta text-white rounded-full hover:bg-terracotta-hover transition-all hover:gap-3" data-testid="hero-cta-book">
                {t(lang, "hero.cta_book")}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" strokeWidth={1.5} />
              </Link>
              <Link to="/services" className="inline-flex items-center gap-2 px-7 py-3.5 border border-ink/20 rounded-full text-ink hover:bg-ink hover:text-sand transition-colors" data-testid="hero-cta-services">
                {t(lang, "hero.cta_services")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] rounded-md overflow-hidden bg-muted relative animate-fade-up" style={{ animationDelay: "200ms" }} data-testid="hero-image-rotator">
              {heroImages.map((src, i) => (
                <img
                  key={`${src}-${i}`}
                  src={resolveUrl(src)}
                  alt="Ayurveda spa composition"
                  loading={i === heroIndex ? "eager" : "lazy"}
                  className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-[1500ms] ease-in-out"
                  style={{ opacity: i === heroIndex ? 1 : 0 }}
                  data-testid={`hero-image-${i}`}
                />
              ))}
            </div>
            <div className="absolute -bottom-8 -left-8 bg-white border border-border rounded-md p-5 max-w-[200px] hidden lg:block animate-fade-up" style={{ animationDelay: "500ms" }}>
              <Leaf className="w-5 h-5 text-sage mb-3" strokeWidth={1.5} />
              <p className="text-sm font-display text-ink leading-snug">Heilkunst des Ayurveda</p>
            </div>
          </div>
        </div>
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-terracotta/5 blur-3xl pointer-events-none" />
      </section>

      {/* SERVICE CATEGORIES — Conversion-optimised entry into the service catalog */}
      <CategoryTiles />

      {/* INTRO STRIP */}
      <section className="bg-muted/60 border-y border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20 text-center">
          <Flower2 className="w-6 h-6 text-terracotta mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="font-display text-3xl lg:text-4xl text-ink leading-tight">{t(lang, "intro.title")}</h2>
          <p className="mt-6 text-ink-muted leading-relaxed max-w-3xl mx-auto">{t(lang, "intro.body")}</p>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-3">— {t(lang, "services.title")}</div>
            <h2 className="font-display text-4xl lg:text-5xl text-ink max-w-2xl leading-tight">{t(lang, "services.subtitle")}</h2>
          </div>
          <Link to="/services" className="text-sm uppercase tracking-wider text-ink-muted hover:text-terracotta inline-flex items-center gap-1.5" data-testid="services-view-all">
            {t(lang, "services.learn_more")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="grid grid-cols-12 gap-5 auto-rows-[200px]">
          {services.slice(0, 6).map((svc, idx) => {
            const spans = [
              "col-span-12 md:col-span-7 row-span-2",
              "col-span-12 md:col-span-5 row-span-1",
              "col-span-6 md:col-span-5 row-span-1",
              "col-span-6 md:col-span-4 row-span-1",
              "col-span-12 md:col-span-4 row-span-1",
              "col-span-12 md:col-span-4 row-span-1",
            ];
            return (
              <Link key={svc.id} to={`/booking?service=${svc.id}`}
                className={`relative group overflow-hidden rounded-md bg-ink ${spans[idx] || "col-span-6 md:col-span-4 row-span-1"}`}
                data-testid={`home-service-${svc.slug}`}
              >
                <img src={svc.image_url} alt={pickMulti(svc.name, lang)} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-6">
                  <h3 className="font-display text-2xl text-white leading-tight">{pickMulti(svc.name, lang)}</h3>
                  <div className="mt-2 flex items-center gap-3 text-xs text-sand/80">
                    <span>{svc.duration_minutes} {t(lang, "services.minutes")}</span>
                    <span className="opacity-50">·</span>
                    <span>CHF {svc.price_chf}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="relative bg-sand-soft border-t border-border">
        <div className="absolute inset-0 opacity-30">
          <img src={TEXTURE_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[3/4] rounded-md overflow-hidden bg-muted">
              <img src="https://customer-assets.emergentagent.com/job_massage-portal-6/artifacts/lhkqyz7i_regula1-1.webp" alt="Regula Müller" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{t(lang, "about.title")}</div>
            <h2 className="font-display text-4xl lg:text-5xl text-ink leading-tight max-w-xl">{t(lang, "about.lead")}</h2>
            <p className="mt-8 text-ink-muted leading-relaxed max-w-xl">{t(lang, "about.body")}</p>
            <Link to="/about" className="mt-10 inline-flex items-center gap-2 text-terracotta hover:gap-3 transition-all" data-testid="home-about-link">
              {t(lang, "services.learn_more")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 text-center">
        <h2 className="font-display text-4xl lg:text-5xl text-ink max-w-2xl mx-auto leading-tight">
          {t(lang, "hero.cta_book")}
        </h2>
        <p className="mt-6 text-ink-muted max-w-xl mx-auto">{t(lang, "booking.subtitle")}</p>
        <Link to="/booking" className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-terracotta text-white rounded-full hover:bg-terracotta-hover transition-colors" data-testid="home-cta-book-bottom">
          {t(lang, "nav.book")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
        </Link>
      </section>
    </div>
  );
}

/**
 * CategoryTiles
 *
 * Homepage conversion entry — 6 service categories with transparent starting
 * prices, short benefit copy and a clear CTA per tile. Fires a GA4
 * `select_content` event on each click so we can measure category → service
 * navigation lift (see docs/ANALYTICS.md).
 *
 * All categories, copy and prices reflect the real Ayurveda Aargau offering
 * (women-only practice, 8 treatments between CHF 60–120). Design uses the
 * existing brand tokens (terracotta / sand / ink, Cormorant Garamond).
 * Fully translated for DE + EN as specified in the Rankly conversion plan;
 * FR / IT fall back to DE (German is the primary local market).
 */
const CATEGORY_COPY = {
  de: {
    eyebrow: "— Unsere Leistungen",
    heading: "Für jede Frau die passende Behandlung.",
    subheading:
      "Von der 30-minütigen Kopfmassage bis zur zweistündigen Ganzkörper-Behandlung – hier findest du auf einen Blick, was dich erwartet und was es kostet.",
    priceFootnote:
      "Alle Preise in CHF inkl. MwSt. · Bezahlung vor Ort per Bar, Twint oder Kreditkarte.",
    bookCta: "Jetzt Termin buchen",
    learnMore: "Mehr erfahren",
    inclusive: "inklusive",
    onRequest: "auf Anfrage",
    fromPrice: (n) => `ab CHF ${n}.–`,
    tiles: {
      "massagen-60": {
        label: "Ayurvedische Massagen",
        body: "Abhyanga, Kalari, Rücken- und Kopf-Fuss-Massage – 60 Minuten tiefe Entspannung.",
      },
      "kurze-auszeiten": {
        label: "Kurze Auszeiten",
        body: "Kopf-, Fuss- und Bauchmassage – 30 Minuten Ruhe für zwischendurch.",
      },
      "garshana-peeling": {
        label: "Garshana Trocken-Peeling",
        body: "Aktivierende Massage mit Rohseide – regt Lymphfluss und Stoffwechsel an.",
      },
      "klangschalen-ritual": {
        label: "Klangschalen-Ritual",
        body: "Tibetische, indische und nepalesische Klangschalen – bei jeder Massage inklusive.",
      },
      "ernaehrungsberatung": {
        label: "Ayurvedische Ernährungsberatung",
        body: "Individuelle Beratung nach deinem Dosha-Typ – vor Ort oder online, ca. 60 Min.",
      },
      "geschenkgutscheine": {
        label: "Geschenkgutscheine",
        body: "Verschenke eine Auszeit – digital oder als hochwertige PDF-Urkunde per Post.",
      },
    },
  },
  en: {
    eyebrow: "— Our Treatments",
    heading: "The right treatment for every woman.",
    subheading:
      "From a 30-minute head massage to a two-hour full-body treatment – see at a glance what to expect and what it costs.",
    priceFootnote:
      "All prices in CHF incl. VAT · On-site payment by cash, Twint or credit card.",
    bookCta: "Book your appointment",
    learnMore: "Learn more",
    inclusive: "included",
    onRequest: "on request",
    fromPrice: (n) => `from CHF ${n}.–`,
    tiles: {
      "massagen-60": {
        label: "Ayurvedic Massages",
        body: "Abhyanga, Kalari, back and head-foot massage – 60 minutes of deep relaxation.",
      },
      "kurze-auszeiten": {
        label: "Short Escapes",
        body: "Head, foot and belly massage – 30 minutes of calm for busy days.",
      },
      "garshana-peeling": {
        label: "Garshana Dry Massage",
        body: "Activating raw-silk massage – stimulates lymph flow and metabolism.",
      },
      "klangschalen-ritual": {
        label: "Singing Bowl Ritual",
        body: "Tibetan, Indian and Nepalese singing bowls – included in every massage.",
      },
      "ernaehrungsberatung": {
        label: "Ayurvedic Nutrition Consulting",
        body: "Individual advice based on your Dosha type – in person or online, approx. 60 min.",
      },
      "geschenkgutscheine": {
        label: "Gift Vouchers",
        body: "Give the gift of an escape – digital or as a premium printed voucher by post.",
      },
    },
  },
};

// Base tile data — visual + navigation + pricing (language-independent)
const CATEGORIES = [
  { id: "massagen-60",         priceNum: 120,   href: "/massagen",    icon: Sparkles, accent: "bg-terracotta/10 text-terracotta" },
  { id: "kurze-auszeiten",     priceNum: 60,    href: "/massagen",    icon: Timer,    accent: "bg-sage/15 text-sage" },
  { id: "garshana-peeling",    priceNum: 100,   href: "/massagen",    icon: Wind,     accent: "bg-terracotta/10 text-terracotta" },
  { id: "klangschalen-ritual", priceType: "inclusive", href: "/klangschalen", icon: Music4, accent: "bg-sage/15 text-sage" },
  { id: "ernaehrungsberatung", priceType: "onRequest", href: "/ernaehrung",   icon: Salad,  accent: "bg-terracotta/10 text-terracotta" },
  { id: "geschenkgutscheine",  priceNum: 60,    href: "/gutscheine",  icon: Gift,     accent: "bg-sage/15 text-sage" },
];

function trackCategoryClick(category) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "select_content", {
        content_type: "service_category",
        item_id: category.id,
        item_name: category.label,
      });
    }
    if (typeof window !== "undefined" && typeof window.fbq === "function" && window.__aaPixelOn) {
      window.fbq("trackCustom", "ServiceCategoryClick", {
        category_id: category.id,
        category_name: category.label,
      });
    }
  } catch (e) {
    // never let analytics break navigation
  }
}

function CategoryTiles() {
  const { lang } = useLang();
  const copy = CATEGORY_COPY[lang] || CATEGORY_COPY.de;

  const priceLabelFor = (tile) => {
    if (tile.priceType === "inclusive") return copy.inclusive;
    if (tile.priceType === "onRequest") return copy.onRequest;
    return copy.fromPrice(tile.priceNum);
  };

  return (
    <section
      className="bg-sand-soft border-b border-border"
      aria-labelledby="categories-heading"
      data-testid="home-categories"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-24">
        <div className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{copy.eyebrow}</div>
          <h2
            id="categories-heading"
            className="font-display text-4xl lg:text-5xl text-ink leading-tight"
          >
            {copy.heading}
          </h2>
          <p className="mt-6 text-ink-muted leading-relaxed">{copy.subheading}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const tileCopy = copy.tiles[c.id];
            const priceLabel = priceLabelFor(c);
            return (
              <Link
                key={c.id}
                to={c.href}
                onClick={() => trackCategoryClick({ id: c.id, label: tileCopy.label })}
                className="group relative flex flex-col bg-white border border-border rounded-md overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-20px_rgba(43,31,22,0.35)]"
                data-testid={`category-tile-${c.id}`}
                aria-label={`${tileCopy.label} – ${priceLabel}`}
              >
                <div className="p-8 flex flex-col h-full">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-6 ${c.accent}`}>
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-2xl text-ink leading-snug mb-3">
                    {tileCopy.label}
                  </h3>
                  <p className="text-sm text-ink-muted leading-relaxed mb-6 flex-1">
                    {tileCopy.body}
                  </p>
                  <div className="flex items-center justify-between pt-5 border-t border-border/70">
                    <span
                      className="text-lg font-display text-terracotta"
                      data-testid={`category-price-${c.id}`}
                    >
                      {priceLabel}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-sm uppercase tracking-wider text-ink group-hover:text-terracotta transition-colors">
                      {copy.learnMore}
                      <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" strokeWidth={1.5} />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 text-sm text-ink-muted">
          <p>{copy.priceFootnote}</p>
          <Link
            to="/booking"
            onClick={() =>
              trackCategoryClick({ id: "all-categories-book", label: "Categories bottom CTA" })
            }
            className="inline-flex items-center gap-2 px-6 py-3 bg-terracotta text-white rounded-full hover:bg-terracotta-hover transition-colors"
            data-testid="home-categories-book-cta"
          >
            {copy.bookCta}
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </section>
  );
}
